/**
 * Textes du lot L08 — « Agents gouvernés et IA souveraine » (ADR 0080), français = référence :
 * passerelle LLM, clés d'un projet, assistant documentaire (RAG), assistants (code, pipeline,
 * description de table), agents, validations, politiques. Branchés dans `fr.ts` sous la clé `ai` ;
 * `ai.en.ts` porte exactement les mêmes clés (typage + `i18n.test.ts`).
 */
export const aiFr = {
  common: {
    platform: 'Plateforme',
    disabled: 'Désactivé',
    yes: 'Oui',
    no: 'Non',
    created: 'Créé',
    updated: 'Mis à jour',
    saved: 'Modifications enregistrées.',
    tokensUnit: 'jetons',
    modelUsage: 'Modèle {{model}} · {{total}} jetons',
    tabs: { summary: 'Résumé', definition: 'Définition' },
  },
  errors: {
    quota: {
      title: 'Budget de jetons épuisé',
      description: 'Le budget de la passerelle pour ce projet est consommé sur la fenêtre en cours.',
      retryIn_one: 'Le budget de ce projet est consommé : nouvel essai possible dans {{count}} seconde.',
      retryIn_other: 'Le budget de ce projet est consommé : nouvel essai possible dans {{count}} secondes.',
    },
    codes: {
      llm_quota_exceeded: 'Le budget de jetons du projet est épuisé sur la fenêtre en cours.',
      AGENT_REVOKED: 'Cet agent est révoqué : toutes ses requêtes sont refusées.',
      APPROVER_IS_AGENT_OWNER:
        'Vous ne pouvez pas décider : vous êtes l’auteur de la politique de cet agent ou propriétaire de son identité.',
      APPROVER_MUST_BE_HUMAN: 'Seule une personne peut décider d’une validation, jamais un jeton machine.',
      APPROVAL_NOT_PENDING: 'Cette demande a déjà été décidée, consommée ou a expiré.',
      REAUTHENTICATION_REQUIRED:
        'Reconnectez-vous pour décider : la connexion doit dater de moins de 5 minutes.',
      PROVIDER_READ_ONLY: 'Le fournisseur de la plateforme est en lecture seule.',
      MODEL_READ_ONLY: 'Les modèles de la plateforme sont en lecture seule.',
      RAG_DOCUMENT_TOO_LARGE: 'Le fichier dépasse 20 Mio.',
      RAG_DOCUMENT_UNSUPPORTED: 'Format non pris en charge : PDF, texte, Markdown, CSV, JSON ou HTML.',
      RAG_DOCUMENT_UNREADABLE: 'Le fichier n’a pas pu être lu (PDF scanné sans texte ?).',
      RAG_DOCUMENT_EMPTY: 'Le document ne contient aucun texte à indexer.',
      RAG_DIMENSION_MISMATCH: 'Le modèle d’embeddings ne correspond pas à la dimension de l’index.',
      AGENT_PRINCIPAL_NOT_FOUND: 'Aucune identité ni application ne porte ce clientId dans l’organisation.',
    },
  },
  llm: {
    title: 'Passerelle LLM',
    subtitle:
      'Les modèles de l’organisation derrière une API compatible OpenAI : fournisseurs, alias de modèles, budgets de jetons par projet et journal des appels.',
    pii: { badge: 'Données personnelles masquées' },
    tabs: { models: 'Modèles', budgets: 'Budgets', usages: 'Journal des appels' },
    providers: {
      title: 'Fournisseurs',
      subtitle:
        'Serveurs compatibles OpenAI (vLLM, Ollama, llama.cpp, TGI…). La clé d’API est scellée : elle n’est jamais réaffichée.',
      new: 'Nouveau fournisseur',
      newTitle: 'Nouveau fournisseur',
      newSubtitle: 'Un serveur de modèles de l’organisation, joint par graal-api.',
      created: 'Fournisseur {{name}} créé.',
      delete: 'Supprimer le fournisseur',
      confirmDelete: 'Supprimer {{name}} ? Ses modèles sont supprimés avec lui.',
      deleted: 'Fournisseur {{name}} supprimé.',
      keySealed: 'Clé scellée',
      noKey: 'Aucune clé',
      platformReadOnly:
        'Fournisseur de la plateforme : il est servi à toutes les organisations et ne se modifie pas ici.',
      empty: {
        title: 'Aucun fournisseur',
        description: 'Déclarez un serveur compatible OpenAI pour y router des modèles.',
      },
      columns: { name: 'Nom', kind: 'Type', baseUrl: 'URL', apiKey: 'Clé d’API' },
      fields: {
        name: 'Nom',
        kind: 'Type de serveur',
        baseUrl: 'URL de l’API',
        baseUrlHelp: 'L’URL de base compatible OpenAI, avec /v1.',
        apiKey: 'Clé d’API',
        apiKeyHelp: 'Facultative. Scellée à l’enregistrement, jamais relue.',
        apiKeyKeep: 'Laissez vide pour garder la clé actuelle ; saisissez-en une pour la remplacer.',
        removeApiKey: 'Retirer la clé d’API',
        enabled: 'Actif',
        description: 'Description',
      },
      errors: { baseUrl: 'Une URL http:// ou https://.' },
      kinds: {
        openai: 'OpenAI (compatible)',
        ollama: 'Ollama',
        vllm: 'vLLM',
        llamacpp: 'llama.cpp',
        tgi: 'TGI',
      },
      check: {
        title: 'Test de connexion',
        hint: 'graal-api appelle GET /models sur ce serveur.',
        run: 'Tester la connexion',
        reachable_one: 'Le serveur répond : {{count}} modèle annoncé.',
        reachable_other: 'Le serveur répond : {{count}} modèles annoncés.',
        unreachable: 'Le serveur ne répond pas.',
      },
    },
    models: {
      title: 'Modèles',
      subtitle:
        'Les alias exposés par la passerelle : le nom que les projets demandent, et le modèle réel qui répond.',
      new: 'Nouveau modèle',
      newTitle: 'Nouveau modèle',
      newSubtitle: 'Un alias de modèle, routé vers un fournisseur.',
      created: 'Modèle {{name}} créé.',
      delete: 'Supprimer le modèle',
      confirmDelete: 'Supprimer l’alias {{name}} ? Les appels qui le nomment seront refusés.',
      deleted: 'Modèle {{name}} supprimé.',
      searchPlaceholder: 'Rechercher un modèle…',
      usageHint: 'Les projets appellent ce modèle par son alias « {{name}} » (champ model de la requête).',
      empty: { title: 'Aucun modèle', description: 'Ajoutez un alias pour exposer un modèle aux projets.' },
      columns: {
        name: 'Alias',
        capability: 'Usage',
        provider: 'Fournisseur',
        upstreamModel: 'Modèle réel',
        contextWindow: 'Contexte',
      },
      fields: {
        name: 'Alias',
        nameHelp: 'Le nom que les projets demandent (ex. mistral-small).',
        capability: 'Usage',
        provider: 'Fournisseur',
        upstreamModel: 'Modèle réel',
        upstreamModelHelp: 'L’identifiant du modèle chez le fournisseur.',
        contextWindow: 'Fenêtre de contexte (jetons)',
        enabled: 'Actif',
      },
      capabilities: { chat: 'Conversation', embedding: 'Embeddings' },
    },
    budgets: {
      title: 'Budgets par projet',
      subtitle:
        'Jetons consommés sur la fenêtre courante (lu à {{when}}). Sans budget écrit, le plafond de la plateforme s’applique.',
      searchPlaceholder: 'Rechercher un projet…',
      written: 'Budget écrit',
      platformDefault: 'Défaut de la plateforme',
      meterLabel: 'Consommation de {{project}}',
      detailTitle: 'Budget de {{project}}',
      detailSubtitle: 'Plafond de jetons de la passerelle pour ce projet, et sa fenêtre en cours.',
      used: 'Consommés',
      reserved: 'Réservés (appels en cours)',
      remaining: 'Restants',
      windowStart: 'Début de la fenêtre',
      windowEnd: 'Fin de la fenêtre',
      reservedHint:
        'Un appel réserve son estimation avant de partir ; la réservation est remplacée par la consommation réelle à la réponse.',
      save: 'Enregistrer le budget',
      saved: 'Budget de {{project}} enregistré.',
      reset: 'Revenir au défaut',
      confirmReset: 'Retirer le budget écrit de {{project}} ? Le plafond de la plateforme s’appliquera.',
      resetDone: '{{project}} revient au plafond de la plateforme.',
      empty: { title: 'Aucun projet', description: 'Les budgets s’écrivent par projet.' },
      columns: {
        project: 'Projet',
        kind: 'Budget',
        period: 'Fenêtre',
        consumption: 'Consommation',
        piiMasking: 'Masquage',
      },
      fields: {
        period: 'Fenêtre',
        tokenLimit: 'Plafond de jetons',
        tokenLimitHelp: 'Au-delà, la passerelle répond 429 jusqu’à la fenêtre suivante.',
        piiMasking: 'Masquer les données personnelles',
        piiMaskingHelp:
          'Courriels, téléphones, IBAN et numéros reconnus sont remplacés avant l’envoi au modèle ; le nombre d’éléments masqués est journalisé.',
      },
      periods: { day: 'Jour', month: 'Mois' },
    },
    usages: {
      title: 'Journal des appels',
      subtitle:
        'Modèle, jetons, latence, statut et appelant de chaque appel — jamais le contenu des échanges.',
      searchPlaceholder: 'Filtrer par modèle, opération, appelant…',
      allProjects: 'Tous les projets',
      period: 'Période',
      since: { '1d': 'Dernières 24 h', '7d': '7 derniers jours', '30d': '30 derniers jours' },
      viaKey: 'Clé de projet',
      principal: { user: 'Utilisateur', client: 'Jeton machine' },
      split: '{{prompt}} + {{completion}}',
      ms: '{{value}} ms',
      masked_one: '{{count}} masqué',
      masked_other: '{{count}} masqués',
      empty: { title: 'Aucun appel', description: 'Aucun appel sur cette période.' },
      columns: {
        when: 'Quand',
        model: 'Modèle',
        project: 'Projet',
        caller: 'Appelant',
        tokens: 'Jetons',
        latency: 'Latence',
        piiMasked: 'Masqués',
        status: 'Statut',
      },
    },
  },
  keys: {
    title: 'Clés d’API de la passerelle',
    subtitle:
      'Une clé appelle la passerelle LLM au nom du projet (budget et masquage du projet). Elle n’est affichée qu’à sa création.',
    new: 'Nouvelle clé',
    newTitle: 'Nouvelle clé d’API',
    newSubtitle: 'La clé ne sera affichée qu’une fois : copiez-la avant de fermer.',
    create: 'Créer la clé',
    revoke: 'Révoquer',
    confirmRevoke: 'Révoquer la clé {{name}} ? Elle sera refusée dès sa prochaine requête.',
    revoked: 'Clé {{name}} révoquée.',
    empty: {
      title: 'Aucune clé',
      description: 'Créez une clé pour appeler la passerelle depuis un job ou un outil.',
    },
    status: { active: 'Active', revoked: 'Révoquée', expired: 'Expirée' },
    columns: {
      name: 'Nom',
      hint: 'Clé',
      status: 'État',
      createdBy: 'Créée par',
      created: 'Créée',
      expires: 'Expire',
      lastUsed: 'Dernier usage',
    },
    fields: {
      name: 'Nom',
      nameHelp: 'À quoi sert la clé (ex. job-resume-incidents).',
      expiresInDays: 'Durée de validité (jours)',
      expiresInDaysHelp: 'De 1 à 3 650 jours.',
    },
    reveal: {
      title: 'Clé {{name}} créée',
      copy: 'Copier la clé',
      copied: 'Clé copiée.',
      once: 'Cette clé ne sera plus jamais affichée. Rangez-la dans un gestionnaire de secrets.',
      missing: 'La réponse ne contenait pas la clé : révoquez-la et créez-en une autre.',
      usage:
        'Appeler la passerelle avec cette clé (lue sur l’entrée standard, jamais écrite dans la commande) :',
      done: 'J’ai copié la clé',
      doneHint: 'Fermer cet encadré efface la clé de l’écran.',
    },
  },
  rag: {
    title: 'Assistant documentaire',
    subtitle:
      'Des index de documents du projet, interrogés en langage naturel : chaque réponse cite les extraits dont elle vient.',
    badge: 'RAG',
    new: 'Nouvel index',
    newTitle: 'Nouvel index documentaire',
    newSubtitle: 'La dimension des vecteurs est fixée par le premier document indexé.',
    created: 'Index {{name}} créé.',
    delete: 'Supprimer l’index',
    confirmDelete: 'Supprimer l’index {{name}} avec ses documents et ses extraits ?',
    deleted: 'Index {{name}} supprimé.',
    detailSubtitle: 'Index documentaire du projet.',
    searchPlaceholder: 'Rechercher un index…',
    empty: {
      title: 'Aucun index',
      description: 'Créez un index, ajoutez-y des documents, puis posez vos questions.',
    },
    columns: {
      name: 'Index',
      documents: 'Documents',
      chunks: 'Extraits',
      embeddingModel: 'Modèle d’embeddings',
      backend: 'Stockage',
    },
    fields: {
      name: 'Nom',
      description: 'Description',
      embeddingModel: 'Modèle d’embeddings',
      embeddingModelHelp: 'Alias d’un modèle « embeddings » ; vide = celui de la plateforme.',
      chunkSize: 'Taille d’un extrait (caractères)',
      chunkSizeHelp: 'De 200 à 8 000.',
      chunkOverlap: 'Recouvrement (caractères)',
      dimension: 'Dimension',
      topK: 'Extraits retenus',
    },
    tabs: { chat: 'Conversation', search: 'Recherche', documents: 'Documents' },
    chat: {
      hint: 'Chaque question part seule, avec les extraits les plus proches ; les jetons sont imputés au projet.',
      question: 'Votre question',
      placeholder: 'Ex. Quelle est la procédure en cas d’incident sur un poste source ?',
      ask: 'Demander',
      thinking: 'Recherche des sources et rédaction…',
      sources: 'Sources',
      cited: 'citée',
      citeLabel: 'Voir la source {{n}}',
    },
    search: {
      query: 'Recherche',
      placeholder: 'Des mots, une phrase…',
      run: 'Chercher',
      none: 'Aucun extrait proche.',
      score: 'score {{score}}',
      ordinal: 'extrait {{n}}',
    },
    documents: {
      title: 'Documents',
      subtitle: 'Chaque document est découpé en extraits, vectorisé par la passerelle et indexé.',
      searchPlaceholder: 'Rechercher un document…',
      added_one: '{{title}} indexé ({{count}} extrait).',
      added_other: '{{title}} indexé ({{count}} extraits).',
      remove: 'Retirer le document',
      removeLabel: 'Retirer {{title}}',
      confirmRemove: 'Retirer {{title}} de l’index ?',
      removed: '{{title}} retiré.',
      empty: {
        title: 'Aucun document',
        description: 'Ajoutez un texte, déposez un fichier ou indexez une table.',
      },
      columns: { title: 'Titre', type: 'Type', size: 'Taille' },
      fields: {
        title: 'Titre',
        source: 'Source',
        sourceHelp: 'Facultative : une URL ou une référence, rendue avec les citations.',
        content: 'Texte',
        query: 'Requête',
        queryHelp: 'En lecture seule, exécutée sous VOS droits ; une ligne par paragraphe.',
        datawarehouse: 'Entrepôt',
        datawarehouseHelp: 'Identifiant d’un entrepôt ; vide = l’entrepôt Trino de l’organisation.',
        maxRows: 'Lignes au plus',
      },
      add: {
        submit: 'Indexer',
        file: 'Déposer un fichier',
        fileInput: 'Fichier à indexer',
        tooLarge: '{{name}} dépasse 20 Mio.',
        text: {
          action: 'Ajouter un texte',
          title: 'Ajouter un texte',
          hint: 'Collez un texte : il est découpé et indexé.',
        },
        table: {
          action: 'Indexer une table',
          title: 'Indexer le résultat d’une requête',
          hint: 'Une requête de lecture sur un entrepôt de l’organisation.',
        },
      },
    },
  },
  assist: {
    code: {
      title: 'Assistant de code',
      subtitle:
        'Expliquer, générer ou compléter du code avec les modèles de l’organisation ; imputé au budget du projet.',
      open: 'Assistant de code',
      sheetSubtitle:
        'Le script du job est prérempli. Rien n’est modifié : copiez le résultat si vous le gardez.',
      run: 'Lancer l’assistant',
      forbidden: 'Votre rôle ne permet pas d’utiliser la passerelle LLM sur ce projet.',
      fields: {
        mode: 'Action',
        language: 'Langage',
        instruction: 'Consigne',
        instructionPlaceholder: 'Ex. Lire un CSV S3 et agréger la consommation par région.',
        code: 'Code',
        cursor: 'Position du curseur',
        cursorHelp: 'En caractères depuis le début ; vide = à la fin.',
      },
      modes: { explain: 'Expliquer', generate: 'Générer', complete: 'Compléter' },
      languages: {
        python: 'Python',
        sql: 'SQL',
        bash: 'Bash',
        scala: 'Scala',
        r: 'R',
        java: 'Java',
        yaml: 'YAML',
      },
      results: { explain: 'Explication', code: 'Proposition' },
    },
    pipeline: {
      action: 'Décrire le pipeline',
      title: 'Décrire le pipeline',
      subtitle:
        'Décrivez le traitement en langage naturel : le modèle propose un DAG low-code, que le moteur vérifie avant de vous le montrer.',
      noProject: 'Ouvrez d’abord un projet : les jetons sont imputés à son budget.',
      run: 'Proposer un pipeline',
      fields: {
        description: 'Description',
        descriptionPlaceholder:
          'Ex. Lire eco2mix en Parquet, ne garder que l’Île-de-France, moyenne de la consommation par mois, écrire le résultat en Parquet.',
        language: 'Moteur visé',
      },
      languages: { pandas: 'Pandas', pyspark: 'PySpark' },
      status: { valid: 'Vérifié par le moteur', invalid: 'Refusé par le moteur', unverified: 'Non vérifié' },
      statusHelp: {
        valid: 'Le moteur low-code a validé ce DAG : il peut être chargé et exécuté.',
        invalid:
          'Le moteur a refusé ce DAG, même après une réparation : relisez la raison avant de le charger.',
        unverified: 'Le moteur n’a pas répondu : seule la structure du DAG est contrôlée.',
      },
      attempts_one: '{{count}} tentative · modèle {{model}}',
      attempts_other: '{{count}} tentatives · modèle {{model}}',
      history: 'Historique des tentatives',
      dag: 'DAG proposé',
      importRefused: 'Ce DAG ne peut pas être ouvert dans le canevas : {{detail}}.',
      load: 'Charger dans l’éditeur',
      loadHint:
        'Remplace le canevas ; rien n’est enregistré tant que vous ne cliquez pas sur « Enregistrer ».',
      loaded: 'Pipeline chargé : relisez-le puis enregistrez.',
    },
    describe: {
      action: 'Générer la description',
      title: 'Décrire {{table}}',
      subtitle:
        'Le modèle lit le schéma et un échantillon (sous vos droits) et propose une description. Rien n’est écrit sans votre accord.',
      preview: 'Générer l’aperçu',
      proposal: 'Proposition',
      sampleRead_one: '{{count}} ligne d’échantillon lue',
      sampleRead_other: '{{count}} lignes d’échantillon lues',
      sampleFailed: 'Échantillon illisible ({{detail}}) : description tirée du schéma seul',
      profile_one: 'Profil de {{count}} colonne',
      profile_other: 'Profil de {{count}} colonnes',
      apply: 'Écrire au catalogue',
      applyHint: 'La table sera marquée « générée » jusqu’à ce qu’une personne valide la description.',
      applied: 'Description de {{table}} écrite au catalogue, marquée « générée ».',
      generatedBadge: 'Description générée',
      fields: {
        project: 'Projet imputé',
        projectHelp: 'Les jetons sont décomptés de son budget.',
        sampleRows: 'Lignes d’échantillon',
        sampleRowsHelp: 'De 1 à 200.',
      },
    },
  },
  agents: {
    title: 'Agents',
    subtitle:
      'Les agents IA connectés à l’organisation, leurs droits, et le fil en direct de leurs appels sur ce projet.',
    badge: 'Gouvernance',
    tabs: { agents: 'Agents', validations: 'Validations' },
    connected: 'Agents connectés',
    connectedSubtitle: 'Agents gouvernés et jetons machine vus dans les 24 dernières heures.',
    ungoverned: 'Aucune politique',
    empty: {
      title: 'Aucun agent',
      description: 'Aucun jeton machine n’a appelé graal-api ces dernières 24 heures.',
    },
    state: { governed: 'Gouverné', revoked: 'Révoqué', ungoverned: 'Non gouverné' },
    columns: {
      name: 'Agent',
      state: 'État',
      rights: 'Droits',
      lastSeen: 'Dernier appel',
      calls: 'Appels (1 h)',
      denied: 'Refus (1 h)',
      pending: 'En attente',
    },
    outcomes: {
      allowed: 'Autorisé',
      denied: 'Refusé',
      approval_required: 'Validation requise',
      approved_replay: 'Rejoué après validation',
    },
    feed: {
      title: 'Fil d’activité en direct',
      subtitle: 'Chaque appel d’un agent sur ce projet, et ce que la gouvernance en a fait.',
      mode: {
        connecting: 'Connexion…',
        live: 'En direct',
        polling: 'Relu toutes les 5 s',
      },
      empty: { title: 'Aucun appel', description: 'Les appels des agents apparaîtront ici au fil de l’eau.' },
    },
  },
  approvals: {
    title: 'Validations',
    subtitle: 'Les actions d’agents qui attendent la décision d’une personne.',
    allStatuses: 'Tous les états',
    detailTitle: 'Demande de validation',
    empty: { title: 'Aucune validation', description: 'Aucune action d’agent n’attend de décision.' },
    status: {
      pending: 'En attente',
      approved: 'Approuvée',
      rejected: 'Refusée',
      consumed: 'Exécutée',
      expired: 'Expirée',
    },
    tabs: { request: 'Demande' },
    columns: {
      requested: 'Demandée',
      agent: 'Agent',
      request: 'Requête',
      summary: 'Résumé',
      status: 'État',
      expires: 'Expire',
    },
    fields: {
      principal: 'clientId',
      operation: 'Opération',
      request: 'Requête',
      decidedBy: 'Décidée par',
      decidedAt: 'Décidée le',
      consumedAt: 'Exécutée le',
      body: 'Corps figé {{type}}',
      noBody: 'Requête sans corps.',
    },
    decision: {
      approve: 'Approuver',
      reject: 'Refuser',
      approveTitle: 'Approuver l’action de l’agent',
      rejectTitle: 'Refuser l’action de l’agent',
      comment: 'Commentaire',
      commentHelp: 'Facultatif ; transmis à l’agent et gardé au journal.',
      reauthHint:
        'Décider exige une connexion de moins de 5 minutes : si besoin, la console vous redemande vos identifiants puis rejoue la décision.',
      rules:
        'L’agent rejouera sa requête une seule fois, à l’identique (méthode, chemin et corps) ; une décision ne vaut que pour cette demande.',
      approved: 'Action approuvée : l’agent peut la rejouer.',
      rejected: 'Action refusée.',
      phase: {
        replaying: 'Reconnexion réussie : décision en cours d’envoi…',
        reauthenticating: 'Redirection vers la connexion…',
      },
    },
  },
  policies: {
    title: 'Politiques d’agents',
    subtitle:
      'Ce que chaque jeton machine peut faire : opérations, projets, plafond horaire, plages horaires et actions soumises à validation.',
    new: 'Nouvelle politique',
    newTitle: 'Nouvelle politique d’agent',
    newSubtitle: 'Appliquée par graal-api à chaque requête de ce jeton, dès la suivante.',
    created: 'Politique {{name}} créée.',
    delete: 'Supprimer la politique',
    confirmDelete: 'Supprimer la politique {{name}} ? L’agent ne sera plus gouverné.',
    deleted: 'Politique {{name}} supprimée.',
    searchPlaceholder: 'Rechercher une politique…',
    active: 'Active',
    allOperations: 'Toutes les opérations',
    allProjects: 'Tous les projets',
    anyTime: 'À toute heure',
    everyDay: 'tous les jours',
    unlimited: 'Sans plafond',
    perHour: '{{count}}/h',
    approvalsCount_one: '{{count}} à valider',
    approvalsCount_other: '{{count}} à valider',
    hoursShort: '{{from}} h–{{to}} h',
    hours: 'De {{from}} h à {{to}} h, {{days}}',
    restore: 'Rétablir',
    confirmRestore: 'Rétablir {{name}} ? Ses requêtes seront de nouveau évaluées par la politique.',
    restored: '{{name}} rétabli.',
    revokedNotice: 'Révoquée par {{by}} : {{reason}}. Toute requête de cet agent est refusée.',
    tabs: { activity: 'Appels récents' },
    empty: {
      title: 'Aucune politique',
      description: 'Sans politique, un jeton machine agit avec ses seuls rôles.',
    },
    errors: { days: 'Des jours ISO, de 1 (lundi) à 7 (dimanche).' },
    days: {
      '1': 'lundi',
      '2': 'mardi',
      '3': 'mercredi',
      '4': 'jeudi',
      '5': 'vendredi',
      '6': 'samedi',
      '7': 'dimanche',
    },
    columns: { name: 'Politique', state: 'État', rights: 'Droits', projects: 'Projets' },
    revoke: {
      action: 'Révoquer',
      title: 'Révoquer {{name}}',
      description: 'Toute requête de cet agent sera refusée dès la suivante, quel que soit son jeton.',
      reason: 'Motif',
      reasonHelp: 'Obligatoire ; gardé avec la révocation et visible des administrateurs.',
      done: '{{name}} révoqué.',
    },
    fields: {
      principalId: 'clientId de l’agent',
      principalIdHelp: 'L’identifiant client de l’identité ou de l’application (Jetons et agents).',
      name: 'Nom',
      nameHelp: 'Vide = le nom de l’identité.',
      description: 'Description',
      allowedOperations: 'Opérations autorisées',
      allowedOperationsHelp: 'Motifs d’operationId du contrat (find*, getJobById…) ; vide = toutes.',
      allowedProjects: 'Projets autorisés',
      allowedProjectsHelp: 'Identifiants de projet ; vide = tous.',
      approvalOperations: 'Soumises à validation',
      approvalOperationsHelp: 'Motifs d’operationId qui attendent la décision d’une personne (delete*…).',
      maxRequestsPerHour: 'Plafond de requêtes par heure',
      maxRequestsPerHourHelp: 'Vide = sans plafond.',
      hoursFrom: 'Actif de (heure)',
      hoursTo: 'Jusqu’à (heure)',
      hoursHelp: 'Heures de 0 à 24, fuseau du serveur ; vide = à toute heure.',
      days: 'Jours actifs',
      daysHelp: '1 = lundi … 7 = dimanche ; vide = tous les jours.',
      activeHours: 'Plages horaires',
      createdBy: 'Créée par',
    },
  },
  tools: {
    codeTitle: 'Assistant de code',
  },
};
